declare var require: any;

import {addLocale, LocaleData, useLocale} from "ttag";

/**
 * 	@class I18n (Internationalisation)
 *
 * 	Adds internationalisation & multiple languages to an app.
 *
 * 	Extends on the (npm) ttag library (https://ttag.js.org/, https://github.com/ttag-org/ttag, https://www.npmjs.com/package/ttag),
 * 	which uses the gettext standard (https://www.gnu.org/software/gettext/, https://en.wikipedia.org/wiki/Gettext).
 *
 * 	Needs both the ttag and ttag-cli packages.
 *
 * 	Translations are managed through .po files, located in the src/i18n folder.
 *
 * 	These files are created with :
 * 		ttag init nl src/i18n/nl.po
 * 		(in terminal), in which the first "nl" represents the chosen locale
 * 		(full list: https://www.gnu.org/software/gettext/manual/html_node/Usual-Language-Codes.html)
 *
 * 	To translate the app, replace normal strings with function calls
 * 		{t`Hello World`}
 * 		see the ttag manual for many more examples: https://ttag.js.org/docs/quickstart.html
 *
 * 	To translate all the sentences, open the .po files, and translate all messages in their respective msgstr value.
 * 	It is possible to add multiple plural forms (https://ttag.js.org/docs/ngettext.html) and context
 * 	(https://ttag.js.org/docs/context.html) as well.
 *
 * 	Once some translations are present, and the file needs to be re-generated due to extra translatable text, use this command:
 *		ttag update src/i18n/en.po src/js/pages/mainlayout.tsx
 *		(in terminal) Do this for all .po files
 *		The new translatables will be recognised, and added to the .po file, without ruining the existing entries
 *
 * 	Typescript/javascript files cannot interprete those .po files, so they need to be converted to .json files. Use:
 * 		ttag po2json src/i18n/en.po > src/i18n/compiled/en.json
 * 		(in terminal) Do this for all .po files
 *
 * 	For more information on this library, refer to the links above.
 *
 * 	This class takes care of both recognizing which locale to use (see docs for the load function) and loading the translations.
 *
 * 	Important: This class should be extended in the project, as this one only knows about the ReactCore translation files.
 * 	The extended class should define:
 * 	 - loadProjectTranslationsFromFile() : just like the loadCoreTranslationsFromFile below, referring to the path with
 * 	 										the translation files.
 * 	 - load() : it needs to call the parent load function, but additionally call the addLocale(this.locale, this.translations)
 * 	 			and useLocale(this.locale), as the translations for the project won't be available if this is not called again.
 *
 * 	One extra note:
 * 	declare of require above is needed because these .json files are "require"d into the code.
 * 		If there is a better way, feel free to improve!
 */
export default abstract class I18n
{
	protected locale: string;
	protected fallback: string;
	protected available: string[];
	protected translations: LocaleData;

	/**
	 * Constructor: stores the available languages, and initiates the locale & translations memvars
	 *
	 * @param string[] availableLanguages An array of locales the app is available in
	 * @param string fallback The fallback locale if no valid one is found
	 */
	public constructor(availableLanguages: string[] = [], fallback: string = "en")
	{
		// by default: not set yet
		this.locale = null;
		this.translations = null;

		// store the available languages (even if no languages given, the fallback should always work)
		this.available = availableLanguages;
		this.fallback = fallback;
	}

	public setAvailableLanguages(availableLanguages: string[]): void
	{
		this.available = availableLanguages;
	}

	public getAvailableLanguages(): string[]
	{
		return this.available;
	}

	public setFallback(fallback: string): void
	{
		this.fallback = fallback;
	}

	/**
	 * Load the correct locale & translations
	 * Based on the following sequence:
	 * 1. parameter value, if set
	 * 2. this.locale, if set
	 * 3. localStorage.locale, if set
	 * 4. .env DEFAULT_LOCALE value, if set	// TODO: do Online Orbit apps use .env?
	 * 5. fallback
	 *
	 * NOTE: this function should be overridden in the project's I18n class, as it needs to call the addLocale & useLocale
	 * itself, otherwise only the ReactCore translations will be available.
	 *
	 * @param string preferredLocale The preferred locale (one of the values in this.available, or "snif-browser", in which case the browser will be probed
	 * @return locale
	 */
	public load(preferredLocale: string = null): string
	{
		// which one to use
		let locale: string = preferredLocale;
		if (!preferredLocale)
		{
			// no param given, check this.locale
			if (this.locale)
				locale = this.locale;
			else
			{
				// no param, not set yet, check localStorage
				let ls = localStorage.getItem("locale");
				if (ls)
					locale = ls;
				else
				{
					// no param, not set yet, no userprefs, check user's browser
					locale = "snif-browser";
				}
			}
		}

		// OK, got the locale. Is it a special one?
		if (locale == "snif-browser")
			locale = this.snifBrowser().substring(0, 2);

		// does the found locale exist in the available locales? If not, fallback to the requested fallback value
		if (this.available.indexOf(locale) < 0)
			locale = this.fallback;

		// OK, found, now load it
		if (locale != this.locale || !this.translations)
		{
			// find the translations
			this.translations = this.loadAllTranslationsForLocale(locale);
			if (this.translations)
				addLocale(locale, this.translations);

			// locale can be used even without translations
			useLocale(locale);

			// store in this.locale
			this.locale = locale;

			// store in localStorage.locale
			localStorage.setItem("locale", this.locale);
		}

		return locale;
	}

	/**
	 * Sniffing the browser's language.
	 * Code used from https://www.npmjs.com/package/detect-browser-language (which Typescript would not accept, so I copied it over)
	 * @return string The preferred language the user has set in its browser
	 */
	private snifBrowser(): string
	{
		return (navigator.languages && navigator.languages[0]) || navigator.language;
	}

	/**
	 * Tries to load all translations for a given locale.
	 * This includes both the translations of the ReactCore and the Project
	 *
	 * @param locale The locale to load the translations for
	 * @return LocaleData | boolean The found LocaleData (including translations), or false upon failure
	 */
	private loadAllTranslationsForLocale(locale: string): LocaleData
	{
		// first: load the ReactCore translations
		const coreTranslations = this.loadCoreTranslationsFromFile(locale);

		// then: load the project's translations
		const projectTranslations = this.loadProjectTranslationsFromFile(locale);

		// now: merge those into one locale file
		let translations: LocaleData = null;
		if (coreTranslations && projectTranslations)
		{
			// both found: merge them!
			translations = projectTranslations;

			for (let context in translations.translations)
				translations.translations[context] = {...coreTranslations.translations[context], ...projectTranslations.translations[context]};
		}
		else if (coreTranslations && !projectTranslations)
		{
			// only core translations found: use these
			// TODO: log a notice as well?
			translations = coreTranslations;
		}
		else if (!coreTranslations && projectTranslations)
		{
			// only project translations found: use these
			// TODO: log a notice as well?
			translations = projectTranslations;
		}

		// OK
		return translations;
	}

	/**
	 * Load translations from the ReactCore for the given locale, and return them.
	 *
	 * @param locale The locale to load the translations for
	 * @return LocaleData | boolean The found LocaleData (including translations), or false upon failure
	 */
	public loadCoreTranslationsFromFile(locale: string): LocaleData
	{
		try
		{
			// try to load the json file containing all locale data & translations
			return require(`./../i18n/compiled/${locale}.json`);
		}
		catch (e)
		{
			// if loading failed (due to file not found for example): we might be using the fallback, which is OK
			return null;
		}
	}

	/**
	 * Function to be overridden in the Project, to read the project's translations file
	 * @param locale The locale to load the translations for
	 * @return LocaleData | boolean The found LocaleData (including translations), or false upon failure
	 */
	protected abstract loadProjectTranslationsFromFile(locale: string): LocaleData;
}